import { create } from 'zustand';
import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';
import {
  SalesforceRecord,
  PaginatedSalesforceRecords,
} from '@/features/dashboard/types/salesforce.types';
import { useAuthStore } from '@/features/auth/stores/auth.store';

// Keep Activity if still used on dashboard for other purposes
interface Activity {
  id: string;
  description: string;
  timestamp: string;
  type: 'link_generated' | 'record_added' | 'application_submitted' | string; // Made type more generic
}

interface DashboardPagination {
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
}

interface DashboardStore {
  // Existing state to keep/adapt:
  loading: boolean; // Generic loading for init, settings marked viewed. Specific one for records below.
  error: string | null; // Generic error. Specific one for records below.
  recentActivity: Activity[];
  viewed_settings: boolean | null;

  // New state for multi-mapping record display:
  selectedFieldMappingId: string | null;
  selectedFieldMappingShortId: string | null;
  recordsForSelectedMapping: SalesforceRecord[];
  selectedRecordId: string | null;
  isLoadingRecords: boolean;
  errorLoadingRecords: string | null;
  searchQuery: string;
  pagination: DashboardPagination;

  // URL customization state
  selectedRecordOwnerId: string | null;
  signerIndex: number | null;

  // Existing actions to keep/adapt:
  initializeSettingsStatus: (status: boolean) => void;
  markSettingsViewed: () => Promise<void>;
  resetDashboardState: () => void;

  // New actions for multi-mapping:
  selectFieldMapping: (mappingId: string | null, mappingShortId: string | null) => Promise<void>;
  fetchRecordsForSelectedMapping: (params: {
    mappingId: string;
    page?: number;
    search?: string;
    pageSize?: number;
  }) => Promise<void>;
  setSearchQuery: (query: string) => Promise<void>; // Changed to async to await fetch
  goToPage: (page: number) => Promise<void>;
  setSelectedRecordId: (recordId: string | null) => void;
  setSelectedRecordOwnerId: (ownerId: string | null) => void;
  setSignerIndex: (index: number | null) => void;
  generateApplicationUrlForSelectedMapping: (recordId?: string) => string | null;
}

const DEFAULT_PAGE_SIZE = 50;
// const LEADS_REFRESH_INTERVAL = 5 * 60 * 1000; // No longer directly applicable in this way

const initialPaginationState: DashboardPagination = {
  currentPage: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalRecords: 0,
  totalPages: 0,
};

export const useDashboardStore = create<DashboardStore>((set, get) => ({
  // Adapted/Kept state
  loading: false, // For general store loading, not records specifically
  error: null,
  recentActivity: [],
  viewed_settings: null,

  // New state initialization
  selectedFieldMappingId: null,
  selectedFieldMappingShortId: null,
  recordsForSelectedMapping: [],
  selectedRecordId: null,
  isLoadingRecords: false,
  errorLoadingRecords: null,
  searchQuery: '',
  pagination: initialPaginationState,

  // URL customization state
  selectedRecordOwnerId: null,
  signerIndex: null,

  // Adapted/Kept actions
  initializeSettingsStatus: (status: boolean) => {
    logger.debug('DashboardStore', 'Initializing viewed_settings status', { status });
    if (get().viewed_settings === null) {
      set({ viewed_settings: status });
    }
  },

  markSettingsViewed: async () => {
    if (get().viewed_settings === true) {
      logger.debug('DashboardStore', 'Settings already marked as viewed.');
      return;
    }
    logger.info('DashboardStore', 'Attempting to mark settings as viewed.');
    try {
      set({ loading: true });
      await api.post('/api/auth/settings-viewed');
      set({ viewed_settings: true, loading: false });
      logger.success('DashboardStore', 'Successfully marked settings as viewed in state.');
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'API error';
      logger.error('DashboardStore', 'Failed to mark settings as viewed via API', {
        error: errorMsg,
      });
      set({ error: `Failed to mark settings viewed: ${errorMsg}`, loading: false });
    }
  },

  resetDashboardState: () => {
    logger.info('DashboardStore', 'Resetting dashboard state');
    set({
      loading: false,
      error: null,
      recentActivity: [], // Consider if this should be reset too
      viewed_settings: get().viewed_settings, // Preserve settings status unless explicitly reset elsewhere

      selectedFieldMappingId: null,
      selectedFieldMappingShortId: null,
      recordsForSelectedMapping: [],
      selectedRecordId: null,
      isLoadingRecords: false,
      errorLoadingRecords: null,
      searchQuery: '',
      pagination: initialPaginationState,

      // Reset URL customization state
      selectedRecordOwnerId: null,
      signerIndex: null,
    });
  },

  // New actions implementation
  selectFieldMapping: async (mappingId: string | null, mappingShortId: string | null) => {
    const currentSelectedId = get().selectedFieldMappingId;
    if (currentSelectedId === mappingId) {
      logger.debug('DashboardStore', 'Mapping already selected', { mappingId });
      // Optionally, force a refresh if needed: await get().fetchRecordsForSelectedMapping({ mappingId });
      return;
    }

    logger.info('DashboardStore', 'Selecting field mapping', { mappingId, mappingShortId });

    set({
      selectedFieldMappingId: mappingId,
      selectedFieldMappingShortId: mappingShortId,
      searchQuery: '', // Reset search query on new mapping selection
      selectedRecordId: null, // Reset selected record
      errorLoadingRecords: null, // Clear previous errors
    });

    if (mappingId) {
      await get().fetchRecordsForSelectedMapping({ mappingId });
    } else {
      set({ recordsForSelectedMapping: [], pagination: initialPaginationState });
    }
  },

  fetchRecordsForSelectedMapping: async (params) => {
    const { mappingId, page, search, pageSize } = params;
    if (!mappingId) {
      logger.warn('DashboardStore', 'fetchRecordsForSelectedMapping called without mappingId');
      set({
        recordsForSelectedMapping: [],
        pagination: initialPaginationState,
        isLoadingRecords: false,
      });
      return;
    }

    const pageToFetch = page || get().pagination.currentPage || 1;
    const searchQueryToUse = search !== undefined ? search : get().searchQuery;
    const pageSizeToUse = pageSize || get().pagination.pageSize || DEFAULT_PAGE_SIZE;

    logger.info('DashboardStore', 'Fetching records for mapping', {
      mappingId,
      pageToFetch,
      searchQueryToUse,
      pageSizeToUse,
    });
    set({ isLoadingRecords: true, errorLoadingRecords: null });

    try {
      const response = await api.get<PaginatedSalesforceRecords>(`/api/crm/records/${mappingId}`, {
        params: { page: pageToFetch, search: searchQueryToUse, limit: pageSizeToUse },
      });

      const { records, totalRecords, currentPage, totalPages } = response.data;
      set({
        recordsForSelectedMapping: records,
        pagination: {
          currentPage,
          pageSize: pageSizeToUse, // Assuming API returns the pageSize used or we can use our const
          totalRecords,
          totalPages,
        },
        isLoadingRecords: false,
      });
      logger.success('DashboardStore', 'Successfully fetched records', {
        count: records.length,
        totalRecords,
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch records';
      logger.error('DashboardStore', 'Error fetching records', { mappingId, error: errorMsg });
      set({
        errorLoadingRecords: errorMsg,
        isLoadingRecords: false,
        recordsForSelectedMapping: [],
      });
    }
  },

  setSearchQuery: async (query: string) => {
    logger.debug('DashboardStore', 'Setting search query', { query });
    set({ searchQuery: query, selectedRecordId: null }); // Reset selected record on new search
    const currentMappingId = get().selectedFieldMappingId;
    if (currentMappingId) {
      // Fetch from page 1 with the new search query
      await get().fetchRecordsForSelectedMapping({
        mappingId: currentMappingId,
        page: 1,
        search: query,
      });
    }
  },

  goToPage: async (page: number) => {
    logger.debug('DashboardStore', 'Going to page', { page });
    const { selectedFieldMappingId, pagination, searchQuery } = get();
    if (selectedFieldMappingId && page > 0 && page <= pagination.totalPages) {
      await get().fetchRecordsForSelectedMapping({
        mappingId: selectedFieldMappingId,
        page,
        search: searchQuery,
      });
    } else {
      logger.warn('DashboardStore', 'goToPage called with invalid page or no selected mapping', {
        page,
        selectedFieldMappingId,
      });
    }
  },

  setSelectedRecordId: (recordId: string | null) => {
    logger.debug('DashboardStore', 'Setting selected record ID', { recordId });
    set({ selectedRecordId: recordId });
  },

  setSelectedRecordOwnerId: (ownerId: string | null) => {
    logger.debug('DashboardStore', 'Setting selected record owner ID', { ownerId });
    set({ selectedRecordOwnerId: ownerId });
  },

  setSignerIndex: (index: number | null) => {
    logger.debug('DashboardStore', 'Setting signer index', { index });
    set({ signerIndex: index });
  },

  generateApplicationUrlForSelectedMapping: (recordId?: string) => {
    const mappingShortId = get().selectedFieldMappingShortId;
    if (!mappingShortId) {
      logger.warn(
        'DashboardStore',
        'generateApplicationUrlForSelectedMapping called without a selected mapping short ID.'
      );
      return null;
    }

    // Get user's short ID and custom domain from auth store
    const authState = useAuthStore.getState();
    const userShortId = authState.appUser?.shortId;
    const customDomain = authState.appUser?.customDomain;
    const customDomainVerified = authState.appUser?.customDomainVerified;

    if (!userShortId) {
      logger.warn(
        'DashboardStore',
        'generateApplicationUrlForSelectedMapping called without user shortId available.'
      );
      return null;
    }

    // Determine environment
    const getEnv = (): 'development' | 'staging' | 'production' => {
      const viteEnv = import.meta.env.VITE_ENV;
      if (viteEnv === 'staging') return 'staging';
      if (viteEnv === 'production') return 'production';
      return 'development';
    };
    const env = getEnv();

    // Generate the host based on custom domain or base URL
    let host: string;
    let protocol: string;

    if (env === 'development') {
      // Use localhost for development
      host = 'localhost:5173';
      protocol = 'http';
    } else {
      protocol = 'https';

      // Priority 1: Use verified custom domain if available
      if (customDomain && customDomainVerified) {
        logger.debug('DashboardStore', 'Using verified custom domain for URL', { customDomain });
        host = customDomain;
      } else {
        // Priority 2: Use VITE_APP_BASE_URL (no company subdomain)
      const baseUrl = import.meta.env.VITE_APP_BASE_URL;
      if (!baseUrl) {
        logger.error(
          'DashboardStore',
          'VITE_APP_BASE_URL is required for staging/production environments'
        );
        return null;
      }

      try {
        const urlObj = new URL(baseUrl);
          host = urlObj.hostname;
      } catch (err) {
        logger.error(
          'DashboardStore',
          'Invalid VITE_APP_BASE_URL provided, unable to construct application URL',
          { error: err }
        );
        return null;
      }
      }
    }

    // Construct the full URL
    const fullUrl = `${protocol}://${host}/application/${userShortId}/${mappingShortId}`;
    const { signerIndex, selectedRecordOwnerId } = get();

    // Build query params - each selection is independent
    const query = new URLSearchParams();
    
    if (recordId) {
      query.set('crmId', String(recordId));
      if (signerIndex !== null) {
        query.set('signerIndex', String(signerIndex));
      }
    }

    if (selectedRecordOwnerId) {
      query.set('recordOwnerId', selectedRecordOwnerId);
    }

    // Return URL with query params if any, otherwise just base URL
    const queryString = query.toString();
    return queryString ? `${fullUrl}?${queryString}` : fullUrl;
  },
}));
